/**
 * @module utils/lenis
 *
 * Smooth-scroll singleton — runs on all devices (off under reduced motion).
 *
 * The scroll-driven GSAP timelines on the Approach page (Truth/Dare pinned
 * carousels in utils/scrollCarousel.js) read each scroll event's position and
 * `tweenTo` the matching beat. Native scrolling can move that position in large
 * jumps — touch momentum (a fling fires events sparsely, in chunks) and even a
 * fast wheel/trackpad flick — so a single jump that crosses several beats lands
 * only on the last one and the in-between choreography is skipped.
 *
 * Lenis interpolates the scroll position frame-by-frame (`smoothWheel` for
 * wheel/trackpad, `syncTouch` for touch momentum) — turning one big jump into a
 * smooth sweep that passes through every beat in order. It's driven off the
 * shared GSAP ticker and wired to `ScrollTrigger.update`, so pins, the header
 * scrub and the carousels all read the same interpolated position.
 *
 * Under reduced motion `initSmoothScroll()` resolves to null and every helper
 * degrades to its native equivalent.
 */

import { loadGsap } from '@/utils/gsap';

const REDUCED_MOTION = window.matchMedia(
	'(prefers-reduced-motion: reduce)'
).matches;

let initPromise = null;
let lenis = null;

function shouldEnable() {
	return !REDUCED_MOTION;
}

// gsap ticker passes elapsed seconds; Lenis.raf expects a millisecond timestamp.
function tick(time) {
	if (lenis) lenis.raf(time * 1000);
}

export function initSmoothScroll() {
	if (initPromise) return initPromise;

	if (!shouldEnable()) {
		initPromise = Promise.resolve(null);
		return initPromise;
	}

	initPromise = Promise.all([import('lenis'), loadGsap()])
		.then(([{ default: Lenis }, { gsap, ScrollTrigger }]) => {
			lenis = new Lenis({
				// We drive the loop off GSAP's ticker (below), not Lenis's own RAF,
				// so smooth scroll and every tween share one clock.
				autoRaf: false,
				// Smooth wheel/trackpad on desktop...
				smoothWheel: true,
				// ...and interpolate touch momentum on mobile, so scroll-driven
				// timelines pass through every beat instead of jumping past them.
				syncTouch: true,
				// Nudge touch scrolling slightly faster (mobile only — touchMultiplier
				// affects touch input, not wheel/trackpad). Default is 1.
				touchMultiplier: 1.1,
			});

			// Recompute triggers off the interpolated position every frame.
			lenis.on('scroll', ScrollTrigger.update);

			gsap.ticker.add(tick);
			// Lenis owns the frame pacing; GSAP's lag smoothing would fight it.
			gsap.ticker.lagSmoothing(0);

			ScrollTrigger.refresh();
			return lenis;
		})
		.catch(error => {
			console.error('SmoothScroll: failed to initialise Lenis', error);
			return null;
		});

	return initPromise;
}

export function getLenis() {
	return lenis;
}

// Force Lenis to re-measure the document. It caches the scrollable height and
// CLAMPS every `scrollTo` target to that cached limit, refreshing it only from
// its own (async) resize observers. Code that grows the document and then scrolls
// into the new space in the same tick — rebuilding a pinned section, whose pin
// spacer is what provides that space (see utils/scrollCarousel) — would otherwise
// have its target silently clamped to the pre-growth maximum. No-ops under
// reduced motion where Lenis isn't running.
export function resyncScrollLimits() {
	if (lenis) lenis.resize();
}

// Scroll to an absolute Y. Routes through Lenis when active so its internal
// target stays in sync; otherwise falls back to native scrolling.
export function scrollToY(y, options = {}) {
	if (lenis) {
		lenis.scrollTo(y, options);
	} else {
		window.scrollTo({
			top: y,
			behavior: options.immediate ? 'auto' : 'smooth',
		});
	}
}

// Subscribers notified whenever the body scroll is locked/unlocked through
// stop/startScroll (nav open, etc.). The custom scroll indicator
// (utils/scrollIndicator.js) uses this to hide itself while scrolling is
// disabled — a scroll bar over a locked page is misleading. Returns an
// unsubscribe fn.
const lockListeners = new Set();
export function onScrollLockChange(callback) {
	lockListeners.add(callback);
	return () => lockListeners.delete(callback);
}
function notifyLock(locked) {
	lockListeners.forEach(cb => cb(locked));
}

// Pause/resume Lenis around a body-scroll lock (e.g. nav open). Without this,
// it keeps accumulating a scroll target behind the locked body and lurches on
// release. No-ops under reduced motion where Lenis isn't running.
export function stopScroll() {
	if (lenis) lenis.stop();
	notifyLock(true);
}

export function startScroll() {
	if (lenis) lenis.start();
	notifyLock(false);
}
